import React from "react"
import { Upload } from "antd"
import { ImageTile as ImageTileDoc } from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  findTile,
} from "features/Dashboard/runtime/dashboardStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"

/** Image types every browser and PowerPoint can show. */
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"]
/** The document carries the image, so it is kept small. */
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024

/**
 * An image fitted to the tile. In Build mode the chosen file is stored in
 * the document as a data URL; nothing is uploaded to a server. Only PNG,
 * JPEG, GIF and WebP files of 2 MB or less are taken.
 */
const ImageTile: React.FC<{ tile: ImageTileDoc }> = ({ tile }) => {
  const mode = useDashboardStore((s) => s.mode)
  const updateTile = useDashboardStore((s) => s.updateTile)
  const pageId = useDashboardStore(
    (s) => findTile(s, tile.id)?.page.id ?? ""
  )
  // Returning false keeps antd from posting the file anywhere.
  const onFile = (file: File): false => {
    if (!IMAGE_TYPES.includes(file.type)) {
      notify.error("Image not added", "Use a PNG, JPEG, GIF or WebP image.")
      return false
    }
    if (file.size > MAX_IMAGE_BYTES) {
      notify.error(
        "Image not added",
        "The image is larger than 2 MB. It is saved inside the dashboard, so use a smaller one."
      )
      return false
    }
    const reader = new FileReader()
    reader.onload = () =>
      updateTile(pageId, tile.id, (t) =>
        t.type === "image" ? { ...t, src: String(reader.result) } : t
      )
    reader.onerror = () =>
      notify.error("Image not added", "The file could not be read.")
    reader.readAsDataURL(file)
    return false
  }
  return (
    <div className="dq-image">
      {tile.src ? (
        <img
          className="dq-image__img"
          src={tile.src}
          alt={tile.title ?? ""}
          style={{ objectFit: tile.fit }}
        />
      ) : (
        <p className="dq-tile__empty">No image</p>
      )}
      {mode === "build" && (
        <Upload
          accept={IMAGE_TYPES.join(",")}
          showUploadList={false}
          beforeUpload={onFile}
        >
          <DataQButton type="secondary" size="small">
            {tile.src ? "Replace image" : "Upload image"}
          </DataQButton>
        </Upload>
      )}
    </div>
  )
}

export default ImageTile
